/* eslint-disable no-console */
import {FC} from "react";
import {ScriptPlaceholderProps} from "@common-core/react-composable/script-injector";
import {
  PlaceholderProps,
  WhenDefined
} from "@common-core/react-composable/custom-elements";
import {
  WebComponentObserver,
  WebComponentLoader
} from "@common-core/react-composable/web-components";
import {useSearchParams} from "../hooks";
import {EmbeddedComponent} from "./EmbeddedComponent";
import {EmbeddedComponentFailure} from "./EmbeddedComponentFailure";
import "./EmbeddedComponentLoader.scss";
import {buildEmbeddedComponentOverride} from "./buildEmbeddedComponentOverride";

export interface EmbeddedComponentProps extends ScriptPlaceholderProps {
  /**
   * The definition of the component to load.
   */
  component: EmbeddedComponent;

  /**
   * Additional attributes to pass to the {@link WebComponentLoader}.
   *
   * Attributes specified here are merged with and will override attributes
   * contained in the component definition, enabling the static
   * component definition to be enriched with point-of-use attributes.
   */
  attributes?: Record<string, any>;

  placeholder?: FC<PlaceholderProps>;
}

/**
 * @see {@link WebComponentLoader}
 */
export const EmbeddedComponentLoader: FC<EmbeddedComponentProps> = ({
  component,
  attributes = {},
  loading = () => null,
  failure = EmbeddedComponentFailure,
  placeholder = () => null
}) => {
  const params = useSearchParams();
  const override = buildEmbeddedComponentOverride(params, component);

  if (override.preload) {
    console.info(`Loading preloaded component from: ${override.script.id}`);
    return (
      <WebComponentObserver
        script-id={override.script.id}
        element={override.element}
        attributes={{
          ...override.attributes,
          ...attributes
        }}
        loading={loading}
        failure={failure}
        placeholder={placeholder}
      />
    );
  }

  console.info(`Loading on-demand component from: ${override.script.id}`);
  return (
    // Wait until the last IDS web component is defined in the registry before loading the embedded component
    <WhenDefined element={"ids-vin-input"} placeholder={placeholder}>
      <WebComponentLoader
        script={override.script}
        element={override.element}
        attributes={{
          ...override.attributes,
          ...attributes
        }}
        loading={loading}
        failure={failure}
        placeholder={placeholder}
      />
    </WhenDefined>
  );
};
